<template>
  <v-card
    :class="{
      'ma-6': !xs,
      'manage-subscriptions-card': true,
      'fit-to-space': !isMobile,
      'is-mobile': isMobile
    }"
  >
    <v-card-item>
      <template v-slot:prepend>
        <v-btn
          icon="mdi-arrow-left-circle-outline"
          variant="flat"
          density="compact"
          @click="$router.go(-1)"
          data-cy="manageSubscriptionsBackButton"
        />
      </template>
      <v-card-title class="header-title">
        {{ $t('app.routes.manageSubscriptions') }}
      </v-card-title>
      <v-card-subtitle
        v-if="!$auth.loading && userSubscriptions !== null && userSubscriptions.length !== 0"
        class="timezoneDetails"
      >
        {{ $t('manageSubscriptions.timezoneDetails') }}
      </v-card-subtitle>
    </v-card-item>
    <v-divider class="divider"></v-divider>
    <v-data-table
      v-if="!$auth.loading && userSubscriptions !== null && userSubscriptions.length !== 0"
      :headers="headers"
      :items="userSubscriptions"
      :single-expand="singleExpand"
      v-model:expanded="expanded"
      :show-expand="enableExpandableRows"
      item-value="activationId"
      :mobile-breakpoint="thresholds.md"
      :class="{
        mobile: isMobile,
        'last-row-expanded': isLastRowExpanded
      }"
      :custom-filter="filterByColumnValueAndDeviceSerial"
      :search="customTableFilter"
      v-model:sort-by="sortedBy"
      v-model:items-per-page="itemsPerPage"
      :items-per-page-text="isMobile ? 'tables.rows' : 'tables.rowsPerPage'"
      :items-per-page-options="[10, 25, 50]"
      fixed-header
      fixed-footer
      data-cy="manageSubscriptionsTable"
      @update:current-items="updateTableComputedItems"
    >
      <template v-slot:top>
        <div class="table-bar">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            :label="$t('tables.search')"
            single-line
            hide-details
            class="search-field"
            data-cy="searchField"
          />
        </div>
      </template>
      <!-- desktop view -->
      <template v-if="!isMobile" v-slot:[`item.actions`]="{ item }">
        <td class="text-left">
          <ActionMenu
            v-if="!isAddOnSub(item.subscriptionId)"
            :item="item"
            :isLogInAs="isLogInAs"
            @cancelSubscriptionOpen="cancelSubscriptionOpen"
            @subscriptionBillingInfoOpen="subscriptionBillingInfoOpen"
          />
        </td>
      </template>
      <!-- mobile view -->
      <template v-else v-slot:body="{ items, headers }">
        <tr v-for="internalItem in items" :key="getTableItem(internalItem).activationId">
          <td class="mobile-list">
            <div class="item-title">
              <ActionMenu
                v-if="!isAddOnSub(getTableItem(internalItem).subscriptionId)"
                :item="getTableItem(internalItem)"
                :isLogInAs="isLogInAs"
                class="ml-n1"
                @cancelSubscriptionOpen="cancelSubscriptionOpen"
                @subscriptionBillingInfoOpen="subscriptionBillingInfoOpen"
              />
              <div class="subscription-name">
                {{ getTableItem(internalItem).subscription }}
              </div>
            </div>
            <ul class="flex-content">
              <li
                v-for="header in getMobileHeaders(headers, getTableItem(internalItem))"
                :key="header.key"
                class="flex-item"
              >
                <span> {{ header.title }}:&nbsp; </span>
                <span>{{ getTableItem(internalItem)[header.key] }}</span>
              </li>
              <li
                v-if="enableExpandableRows && itemCanBeExpanded(getTableItem(internalItem))"
                class="flex-item expandable-option-mobile"
              >
                <TableExpandButton
                  :isExpanded="isRowExpanded(getTableItem(internalItem).activationId)"
                  @click="toggleExpandedById(getTableItem(internalItem).activationId)"
                  class="ml-n4 mr-2"
                  data-cy="expandButton"
                />
                <span> {{ $t('manageSubscriptions.devices') }}</span>
              </li>
              <li
                v-if="isRowExpanded(getTableItem(internalItem).activationId)"
                class="flex-item expandable-content-column mobile"
              >
                <ExpandableContent :item="getTableItem(internalItem)" />
              </li>
            </ul>
          </td>
        </tr>
      </template>
      <!-- Show the expand button when the subscription has devices -->
      <template
        v-slot:[`item.data-table-expand`]="{ item, internalItem, isExpanded, toggleExpand }"
      >
        <TableExpandButton
          v-if="itemCanBeExpanded(item)"
          :isExpanded="isExpanded(internalItem)"
          @click="toggleExpand(internalItem)"
          data-cy="expandButton"
        />
      </template>
      <template v-slot:expanded-row="{ item }">
        <td :colspan="11" class="expandable-content-column">
          <ExpandableContent :item="item" />
        </td>
      </template>
      <template v-if="isMobile" v-slot:[`body.prepend`]>
        <ExpandCollapseButtons
          v-if="enableExpandableRows"
          @expandAll="expandAllDevices"
          @collapseAll="collapseAllDevices"
        />
      </template>
      <template v-if="!isMobile" v-slot:[`footer.prepend`]>
        <ExpandCollapseButtons
          v-if="enableExpandableRows"
          @expandAll="expandAllDevices"
          @collapseAll="collapseAllDevices"
        />
      </template>
    </v-data-table>
    <!-- empty message -->
    <div
      v-else-if="!$auth.loading && userSubscriptions !== null && userSubscriptions.length === 0"
      class="text-center emptyMessage"
    >
      {{ $t('manageSubscriptions.emptyMessage') }}
    </div>
    <!-- progress spinner -->
    <div v-else class="text-center emptyMessage">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <SubscriptionCancelDialog
      :open="dialog.subscriptionCancel.open"
      :activationId="dialog.subscriptionCancel.activationId"
      @close="cancelSubscriptionClosed"
    />
    <SubscriptionBillingInfoDialog
      :open="dialog.subscriptionBillingInfo.open"
      :activationId="dialog.subscriptionBillingInfo.activationId"
      :subscriptionId="dialog.subscriptionBillingInfo.subscriptionId"
      @close="subscriptionBillingInfoClosed"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent, toRaw } from 'vue'
import { useDisplay } from 'vuetify'
import type { DataTableHeader, DataTableSortItem } from 'vuetify'
import { FusionStores } from '@fusion/saas-components-v3'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { SUBSCRIPTIONS_GETTERS } from '@/store/subscriptions'
import type { EnrichedSubscription } from '@/store/subscriptions/types'
import {
  deviceIdToSerial,
  paymentTypes,
  subscriptionTiers,
  genericTSILinkSubscriptionTiers
} from '@/store/subscriptions/utils'
import type { SubscriptionBillingInfo } from '@/types/subscriptions'
import { deviceModelDisplay, deviceSubmodelDisplay } from '@/services/map'
import { FeatureFlag } from '@/services/feature-flags'
import { getUserAccountId } from '@/helpers/loginas/logInAsHelper'
import SubscriptionCancelDialog from '@/components/subscriptionCancel/SubscriptionCancelDialog.vue'
import SubscriptionBillingInfoDialog from '@/components/dialogs/SubscriptionBillingInfoDialog.vue'
import ActionMenu from '@/components/manageSubscriptions/table/ActionMenu.vue'
import ExpandCollapseButtons from '@/components/common/buttons/ExpandCollapseButtons.vue'
import TableExpandButton from '@/components/common/buttons/TableExpandButton.vue'
import ExpandableContent from '@/components/manageSubscriptions/table/ExpandableContent.vue'

const LOCAL_STORAGE_KEYS = 'tsi.subscriptionsTableSettings'

type StorageUnits = 'MB' | 'GB'
type StorageValue = [number, StorageUnits]
type StorageState = StorageValue | -1

interface StudyHardware {
  serial: string
  model: string
  name: string
}

interface SubscriptionDevice {
  name: string
  type: string
  serial: string
}

export interface SubscriptionTableRow {
  activationId: string
  subscription: string
  subscriptionId: string
  type: string
  deviceIds: string[] | null
  devices: SubscriptionDevice[]
  deviceQuantityUsed: number | string
  deviceQuantityAllowance: number | string
  activationDate: string
  renewalDate: string
  endDate: string
  billingMethod: string
  paymentDetails: string
  allowCancellation: boolean
}

type TableFilterItem = {
  raw: SubscriptionTableRow
}

interface MobileHeader {
  key: keyof SubscriptionTableRow
  title: string
}

interface TableSettings {
  itemsPerPage: number
  sortBy: DataTableSortItem[]
}

interface SubscriptionDialogState {
  activationId: string
  subscriptionId?: string
  open: boolean
}

export default defineComponent({
  name: 'ManageSubscriptions',
  components: {
    SubscriptionCancelDialog,
    SubscriptionBillingInfoDialog,
    ExpandCollapseButtons,
    ExpandableContent,
    ActionMenu,
    TableExpandButton
  },
  setup() {
    const { xs, smAndDown, thresholds } = useDisplay()
    const loginas = FusionStores.loginas()
    const featureFlagsStore = FusionStores.featureFlags()
    return { xs, smAndDown, thresholds, loginas, featureFlagsStore }
  },
  async mounted(): Promise<void> {
    this.accountId = getUserAccountId(this.$auth)
    await this.getLatestStudyRegistrationHardware()
    await this.getFileImportCount()
    await this.getTotalStorageUsed()
    //make sure the additional add on amounts gets added as soon as api results complete
    this.updateTableComputedItems(this.tableComputedItems)
    this.getSubscriptionsTableSettings()
  },
  data() {
    return {
      sapAccountCount: -1,
      cloudAccountCount: -1,
      sapAccountStorage: -1 as StorageState,
      cloudAccountStorage: -1 as StorageState,
      studyGatewayDevices: new Map<string, StudyHardware>(),
      studyDevices: new Map<string, StudyHardware>(),
      accountId: null as string | null,
      dialog: {
        subscriptionCancel: {
          activationId: '',
          open: false
        } as SubscriptionDialogState,
        subscriptionBillingInfo: {
          activationId: '',
          subscriptionId: '',
          open: false
        } as SubscriptionDialogState
      },
      sortedBy: [{ key: 'type', order: 'asc' }] as DataTableSortItem[],
      itemsPerPage: 10,
      search: '',
      headers: [
        {
          key: 'data-table-expand',
          title: '',
          sortable: false
        },
        {
          title: this.$t('manageSubscriptions.tableHeaders.subscriptionTier'),
          align: 'start',
          key: 'subscription',
          minWidth: 130
        },
        {
          title: this.$t('manageSubscriptions.tableHeaders.type'),
          key: 'type'
        },
        {
          title: this.$t('manageSubscriptions.tableHeaders.deviceQuantityUsed'),
          key: 'deviceQuantityUsed'
        },
        {
          title: this.$t('manageSubscriptions.tableHeaders.deviceQuantityAllowance'),
          key: 'deviceQuantityAllowance'
        },
        {
          title: this.$t('manageSubscriptions.tableHeaders.activationDate'),
          key: 'activationDate'
        },
        {
          title: this.$t('manageSubscriptions.tableHeaders.renewalDate'),
          key: 'renewalDate'
        },
        {
          title: this.$t('manageSubscriptions.tableHeaders.endDate'),
          key: 'endDate'
        },
        {
          title: this.$t('manageSubscriptions.tableHeaders.billingMethod'),
          key: 'billingMethod'
        },
        {
          title: this.$t('manageSubscriptions.tableHeaders.paymentDetails'),
          key: 'paymentDetails'
        },
        {
          title: this.$t('manageSubscriptions.tableHeaders.actions'),
          key: 'actions',
          sortable: false
        }
      ] as DataTableHeader<SubscriptionTableRow>[],
      expanded: [] as string[],
      singleExpand: false,
      tableComputedItems: [] as SubscriptionTableRow[]
    }
  },
  watch: {
    sortedBy: {
      deep: true,
      handler(): void {
        this.setSubscriptionsTableSettings()
      }
    },
    itemsPerPage(): void {
      this.setSubscriptionsTableSettings()
    },
    search(): void {
      this.collapseAllDevices()
    }
  },
  methods: {
    getTableItem(item: SubscriptionTableRow | TableFilterItem): SubscriptionTableRow {
      return 'raw' in item ? item.raw : item
    },
    /**
     * Data return will be in this format:
      [
        {
            "_id": "65b17166f882e3a8d5f5b362",
            "studies_id": "75b17166f882e3a8d5f5b362",
            "timestamp": "2024-01-24T20:21:57.745Z",
            "gateway_serial": "789776666600",
            "gateway_model": "7897",
            "gateway_name": "My SmartBridge",
            "device_serial": "759312kh43lkj43l3j43",
            "device_model": "8534",
            "device_name": "8534 DustTrak drx"
        },
        {
            "_id": "65b17166f882e3a8d5f5b361",
            "studies_id": "75b17166f882e3a8d5f5b361",
            "timestamp": "2024-01-24T20:21:57.744Z",
            "gateway_serial": "75933483942308423",
            "gateway_model": "7593-00",
            "gateway_name": "Smart Station",
            "device_serial": "7591017397924792",
            "device_model": "7591-01",
            "device_name": "PM1 Module"
        },
        {
            "_id": "65b17166f882e3a8d5f5b360",
            "studies_id": "75b17166f882e3a8d5f5b360",
            "timestamp": "2024-01-24T20:21:57.743Z",
            "gateway_serial": "",
            "gateway_model": "",
            "gateway_name": "",
            "device_serial": "7591017397924792",
            "device_model": "",
            "device_name": ""
        },
      ]
    */
    async getLatestStudyRegistrationHardware(): Promise<void> {
      const resp = await this.$api.getLatestStudyRegistrations()
      if (!resp.ok) return

      const hardware = await resp.json()
      this.studyGatewayDevices = new Map()
      this.studyDevices = new Map()

      /**
       * The logic here is to map the latest study registration gateway serials to serial/model/name and device serials to serial/model/name.
       * As studies are registered the names of the gateway and device names could change so this sets the last known hardware configuration.
       * The api returns those registered studies with hardware info in order of latest registered so we only have to set the first
       * gateway and device by serial mapping and can ignore older registered study data.
       */
      hardware.forEach((entry) => {
        if (entry.gateway_serial && !this.studyGatewayDevices.has(entry.gateway_serial)) {
          this.studyGatewayDevices.set(entry.gateway_serial, {
            serial: entry.gateway_serial,
            model: entry.gateway_model?.trim() || '',
            name: entry.gateway_name?.trim() || ''
          })
        }
        if (entry.device_serial && !this.studyDevices.has(entry.device_serial)) {
          this.studyDevices.set(entry.device_serial, {
            serial: entry.device_serial,
            model: entry.device_model?.trim() || '',
            name: entry.device_name?.trim() || ''
          })
        }
      })
    },
    async getFileImportCount(): Promise<void> {
      try {
        const resp = await this.$api.getFileImportCount()
        if (!resp.ok) return
        const countData = await resp.json()
        // store the counts in component's data properties
        this.sapAccountCount = countData.sap_account_id
        this.cloudAccountCount = countData.cloud_account_id
      } catch {
        // ignore - handled in updateTableComputedItems
      }
    },
    /**
     * Data will return in this format:
     * {
        "cloud_account_size_in_bytes": 9370,
        "sap_account_size_in_bytes": 26814
      }
     */
    async getTotalStorageUsed(): Promise<void> {
      try {
        const resp = await this.$api.getTotalStorageUsed()
        if (!resp.ok) return
        const storageData = await resp.json()
        // convert the bytes and store the size and units in component's data properties
        this.sapAccountStorage = this.convertStorageUnits(storageData.sap_account_size_in_bytes)
        this.cloudAccountStorage = this.convertStorageUnits(storageData.cloud_account_size_in_bytes)
      } catch {
        // ignore - handled in updateTableComputedItems
      }
    },
    /**
     * Takes in bytes and returns an array with [value, units]
     * Uses MB until the bytes meet or exceed the equivalent of .1 GB
     * Round up to 2 decimal places
     */
    convertStorageUnits(bytes: number): StorageValue {
      const oneTenthGB = 100000000
      // if < 0.1 GB, convert to MB
      if (bytes < oneTenthGB) {
        let value = bytes / 10000
        value = Math.round(value)
        return [value / 100, 'MB']
      } else {
        // else >= 0.1 GB, convert to GB
        let value = bytes / 10000000
        value = Math.round(value)
        return [value / 100, 'GB']
      }
    },
    subIsGenericTSILink(subId: string): boolean {
      return (genericTSILinkSubscriptionTiers as readonly string[]).includes(subId)
    },
    cancelSubscriptionClosed(): void {
      this.dialog.subscriptionCancel.open = false
    },
    cancelSubscriptionOpen(activationId: string): void {
      this.dialog.subscriptionCancel.open = true
      this.dialog.subscriptionCancel.activationId = activationId
    },
    subscriptionBillingInfoClosed(): void {
      this.dialog.subscriptionBillingInfo.open = false
    },
    subscriptionBillingInfoOpen(activationId: string, subscriptionId: string): void {
      this.dialog.subscriptionBillingInfo.open = true
      this.dialog.subscriptionBillingInfo.activationId = activationId
      this.dialog.subscriptionBillingInfo.subscriptionId = subscriptionId
    },
    isAddOnSub(subId: string): boolean {
      return this.isDataStorage(subId) || this.isFileImport(subId)
    },
    isDataStorage(subId: string): boolean {
      return subId === subscriptionTiers.TSILINK_ADD_ON_DATA_STORAGE
    },
    isFileImport(subId: string): boolean {
      return subId === subscriptionTiers.TSILINK_ADD_ON_FILE_IMPORT
    },
    // Note: Smart Bridge Device Add On will use quantity for devices
    // (unlike file import and data storage Add-ons)
    isSmartBridgeDeviceFeeAddOn(subId: string): boolean {
      return subId === subscriptionTiers.TSILINK_ADD_ON_SMARTBRIDGE_DEVICE_FEE
    },
    determineQuantityUsed(subs: EnrichedSubscription, subItem: number): number | string {
      if (this.isAddOnSub(subs.id)) return ' '
      return Array.isArray(subs.used) ? subs.used[subItem] : subs.used
    },
    determineQuantityAllowance(subs: EnrichedSubscription, subItem: number): number {
      const quantityAllowance = Array.isArray(subs.qty) ? subs.qty[subItem] : subs.qty
      if (this.isFileImport(subs.id)) return quantityAllowance * 200
      return quantityAllowance
    },
    subscriptionToTableHelper(userSubs: EnrichedSubscription[]): SubscriptionTableRow[] {
      const tableSubs: SubscriptionTableRow[] = []
      const dataStorageSubIndex: Record<string, number> = {}
      const fileImportSubIndex: Record<string, number> = {}

      for (const subs of userSubs) {
        // Activation ids will always be present per subscription regardless of activation status
        for (let y = 0; y < subs.activationIds.length; y++) {
          const isAddOnSub = this.isAddOnSub(subs.id)
          const isDataStorage = this.isDataStorage(subs.id)
          const isFileImport = this.isFileImport(subs.id)
          const type = this.$store.getters[
            `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA_TYPE_NAME_BY_ID}`
          ](subs.id) as string
          const deviceQuantityUsed = this.determineQuantityUsed(subs, y)
          const deviceQuantityAllowance = this.determineQuantityAllowance(subs, y)
          const determinedRenewalDate = isAddOnSub
            ? this.$t('manageSubscriptions.notApplicable')
            : this.determineRenewalDate(subs, y)

          // create one row for add-on subscriptions
          if (isDataStorage) {
            if (Object.hasOwn(dataStorageSubIndex, subs.id)) {
              // add to existing row quantity allowance
              const row = tableSubs[dataStorageSubIndex[subs.id]]
              row.deviceQuantityAllowance =
                Number(row.deviceQuantityAllowance) + deviceQuantityAllowance
              continue // skip to next iteration to avoid making a new row
            }
            // store row if doesn't exist yet
            dataStorageSubIndex[subs.id] = tableSubs.length
          }

          if (isFileImport) {
            if (Object.hasOwn(fileImportSubIndex, subs.id)) {
              // add to existing row quantity allowance
              const row = tableSubs[fileImportSubIndex[subs.id]]
              row.deviceQuantityAllowance =
                Number(row.deviceQuantityAllowance) + deviceQuantityAllowance
              continue // skip to next iteration to avoid making a new row
            }
            // store row if doesn't exist yet
            fileImportSubIndex[subs.id] = tableSubs.length
          }

          tableSubs.push({
            activationId: subs.activationIds[y],
            subscription: this.$store.getters[
              `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA_TIER_NAME_BY_ID}`
            ](subs.id) as string,
            subscriptionId: subs.id,
            type,
            deviceIds: subs.deviceIds[y],
            devices: this.getSubscriptionDevices(subs.deviceIds[y], subs.id),
            deviceQuantityUsed,
            deviceQuantityAllowance,
            activationDate: this.determineActivationDate(subs, y),
            renewalDate: determinedRenewalDate,
            endDate: this.determineEndDate(subs, y),
            billingMethod: this.determineBillingMethod(subs, y),
            paymentDetails: this.determinePaymentDetails(subs, y),
            allowCancellation: determinedRenewalDate !== this.$t('manageSubscriptions.canceled')
          })
        }
      }

      this.applySubscriptionDisplayOverrides(tableSubs)
      return tableSubs
    },
    /**
     * Applies display-only overrides for generic TSI Link subs (7593-xx) and
     * Add-ons (File Import, Data Storage): N/A placeholders, GB suffixing,
     * and computed usage text. Applied once here, at the source used to
     * build `userSubscriptions`, so both the default (non-slot) table
     * columns and the mobile view render consistent, correct values.
     * Mutating rows later (e.g. via `@update:current-items`) does not
     * reliably work because Vuetify snapshots each row's column values
     * once and does not react to mutation of plain (non-reactive) objects.
     */
    applySubscriptionDisplayOverrides(tableSubs: SubscriptionTableRow[]): void {
      const NA = this.$t('manageSubscriptions.notApplicable')
      tableSubs.forEach((item) => {
        const subId = item.subscriptionId
        if (
          this.subIsGenericTSILink(subId) &&
          !this.isAddOnSub(subId) &&
          !this.isSmartBridgeDeviceFeeAddOn(subId)
        ) {
          item.deviceQuantityUsed = NA
          item.deviceQuantityAllowance = NA
        }

        if (this.isDataStorage(subId)) {
          // append GB to data storage displayed value
          const quantityAllowance = String(item.deviceQuantityAllowance)
          if (!quantityAllowance.includes('GB')) {
            item.deviceQuantityAllowance = `${quantityAllowance} GB`
          }

          // Check if API call was successful. If so, show storage amounts. If not, leave field blank
          if (this.sapAccountStorage !== -1 && this.cloudAccountStorage !== -1) {
            const [sapSize, sapUnits] = this.sapAccountStorage
            const [cloudSize, cloudUnits] = this.cloudAccountStorage
            item.deviceQuantityUsed = this.$t('manageSubscriptions.quantityUsedAddons', {
              sapQuantity: `${sapSize} ${sapUnits}`,
              cloudQuantity: `${cloudSize} ${cloudUnits}`
            })
          }
        }

        if (
          this.enableFileCount && // feature flag enabled
          this.isFileImport(subId) &&
          this.sapAccountCount !== -1 &&
          this.cloudAccountCount !== -1
        ) {
          item.deviceQuantityUsed = this.$t('manageSubscriptions.quantityUsedAddons', {
            sapQuantity: this.sapAccountCount,
            cloudQuantity: this.cloudAccountCount
          })
        }

        if (this.isAddOnSub(subId) && !this.isSmartBridgeDeviceFeeAddOn(subId)) {
          item.activationDate = NA
          item.renewalDate = NA
          item.endDate = NA
          item.billingMethod = NA
          item.paymentDetails = NA
        }
      })
    },
    getBillingInfoEntry(
      subData: EnrichedSubscription,
      index: number
    ): SubscriptionBillingInfo | null {
      return subData.billingInfo[index] || null
    },
    determineBillingMethod(subData: EnrichedSubscription, index: number): string {
      if (this.isPurchaseOrderPaymentType(subData, index)) {
        return this.$t('manageSubscriptions.billingMethods.purchaseOrder')
      }
      if (this.isCreditCardPaymentType(subData, index)) {
        return this.$t('manageSubscriptions.billingMethods.card')
      }
      return this.$t('manageSubscriptions.notApplicable')
    },
    determinePaymentDetails(subData: EnrichedSubscription, index: number): string {
      const billingInfo = this.getBillingInfoEntry(subData, index)
      if (this.isPurchaseOrderPaymentType(subData, index) && billingInfo?.purchaseOrder) {
        return billingInfo.purchaseOrder
      }
      if (this.isCreditCardPaymentType(subData, index) && billingInfo?.ccNumLastFour) {
        return `****-${billingInfo.ccNumLastFour}`
      }
      return this.$t('manageSubscriptions.notApplicable')
    },
    determineActivationDate(subData: EnrichedSubscription, index: number): string {
      //This date comes in as an UTC date and time string. Ex: 2022-02-16T00:00:00.000Z.
      const activationDate = subData.activationDate[index]
      return activationDate
        ? this.convertUTCToTSITimezoneDate(activationDate)
        : this.$t('manageSubscriptions.notActivated')
    },
    determineRenewalDate(subData: EnrichedSubscription, index: number): string {
      //This date comes in as an CST date and time string without the 'Z' (weird I know). Ex: 2023-02-15T00:00:00
      if (subData.expires[index] && subData.canceled?.[index]) {
        return this.$t('manageSubscriptions.canceled')
      }
      if (subData.canceled?.[index]) {
        return this.$t('manageSubscriptions.cancellationPending')
      }
      const autoRenewalDate = this.getBillingInfoEntry(subData, index)?.autoRenewalDate
      if (autoRenewalDate) {
        return this.convertToDateString(autoRenewalDate)
      }
      return this.$t('manageSubscriptions.notApplicable')
    },
    determineEndDate(subData: EnrichedSubscription, index: number): string {
      //This date comes in as a CST date. Ex: 2023-02-15
      const endDate = subData.expires[index]
      return endDate
        ? this.convertToDateString(endDate)
        : this.$t('manageSubscriptions.notApplicable')
    },
    convertToDateString(str: string): string {
      //Ex: 2023-02-15 or 2023-02-15T00:00:00 -> 02/15/2023
      try {
        const cstDateParts = str.split('-')
        cstDateParts[2] = cstDateParts[2].split('T')[0]
        return `${cstDateParts[1]}/${cstDateParts[2]}/${cstDateParts[0]}`
      } catch {
        // ignore, display str instead
      }
      //if conversion goes wrong just return the string so we don't stop displaying the table
      return str
    },
    convertUTCToTSITimezoneDate(str: string): string {
      //Ex: 2022-02-16T00:00:00.000Z -> 02/16/2022
      const d = new Date(str)
      if (!isNaN(d.getTime())) {
        // date object is valid
        return d.toLocaleString('en-US', {
          timeZone: 'America/Chicago',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
      }
      //if conversion goes wrong just return the string so we don't stop displaying the table
      return str
    },
    isPurchaseOrderPaymentType(subData: EnrichedSubscription, index: number): boolean {
      return this.getBillingInfoEntry(subData, index)?.paymentType === paymentTypes.PO
    },
    isCreditCardPaymentType(subData: EnrichedSubscription, index: number): boolean {
      return this.getBillingInfoEntry(subData, index)?.paymentType === paymentTypes.CC
    },
    setSubscriptionsTableSettings(): void {
      const save: TableSettings = {
        itemsPerPage: this.itemsPerPage,
        sortBy: this.sortedBy
      }
      localStorage.setItem(LOCAL_STORAGE_KEYS, JSON.stringify(save))
    },
    getSubscriptionsTableSettings(): void {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS)
      if (raw === null) return
      try {
        const settings = JSON.parse(raw)
        if (settings.itemsPerPage) {
          this.itemsPerPage = settings.itemsPerPage
        }
        if (Array.isArray(settings.sortBy) && settings.sortBy.length > 0) {
          if (typeof settings.sortBy[0] === 'string') {
            // Old format: sortBy is string[], sortDesc is boolean[] — migrate to DataTableSortItem[]
            const sortDesc: boolean[] = Array.isArray(settings.sortDesc) ? settings.sortDesc : []
            this.sortedBy = settings.sortBy.map((key: string, i: number) => ({
              key,
              order: (sortDesc[i] ? 'desc' : 'asc') as 'asc' | 'desc'
            }))
          } else {
            // New format: sortBy is DataTableSortItem[]
            this.sortedBy = settings.sortBy as DataTableSortItem[]
          }
        }
      } catch {
        // Ignore malformed localStorage data, use defaults
      }
    },
    mapStudyBasedSubscriptionDevicesInfo(
      deviceIds: string[],
      ownedDevices: SubscriptionDevice[]
    ): SubscriptionDevice[] {
      const devices: SubscriptionDevice[] = []
      deviceIds.forEach((serial) => {
        const found = ownedDevices.find((device) => device.serial === serial)
        if (found) {
          devices.push(found)
        } else {
          let hardware = this.getGatewayDeviceDetailsByStudyTypeSerial(serial)
          if (hardware) {
            devices.push(hardware)
          }
          hardware = this.getDeviceDetailsByStudyTypeSerial(serial)
          if (hardware) {
            devices.push(hardware)
          }
        }
      })
      return devices
    },
    mapSubscriptionDevicesInfo(deviceIds: string[], ignoreOwned = false): SubscriptionDevice[] {
      const devices: SubscriptionDevice[] = []
      deviceIds.forEach((deviceModelAndSerial) => {
        const serial = deviceIdToSerial(deviceModelAndSerial)
        const deviceId = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_DEVICE_ID_BY_DEVICE_SERIAL}`
        ](serial) as string
        if (deviceId && (ignoreOwned || this.isDeviceOwned(deviceId))) {
          devices.push({
            name:
              (this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`](
                deviceId
              ) as string) || this.$t('manageSubscriptions.notApplicable'),
            type:
              this.getDeviceTypeDetails(deviceId) || this.$t('manageSubscriptions.notApplicable'),
            serial
          })
        }
      })
      return devices
    },
    getSubscriptionDevices(deviceIds: string[] | null, subId: string): SubscriptionDevice[] {
      let devices: SubscriptionDevice[] = []
      if (deviceIds?.length) {
        if (
          subId === subscriptionTiers.TSILINK_STANDARD ||
          subId === subscriptionTiers.TSILINK_PLUS
        ) {
          devices = this.mapSubscriptionDevicesInfo(deviceIds, true)
          devices = this.mapStudyBasedSubscriptionDevicesInfo(deviceIds, devices)
        } else {
          devices = this.mapSubscriptionDevicesInfo(deviceIds)
        }
      }
      return devices
    },
    getStudyModelType(deviceModel: string): string {
      const key = `model.${deviceModel}`
      if (!this.$te(key)) {
        return this.$t('manageSubscriptions.notApplicable')
      }
      return this.$t(key)
    },
    getGatewayDeviceDetailsByStudyTypeSerial(serial: string): SubscriptionDevice | null {
      if (this.studyGatewayDevices.has(serial)) {
        const hardware = this.studyGatewayDevices.get(serial)
        if (!hardware) return null
        return {
          name: hardware.name || this.$t('manageSubscriptions.notApplicable'),
          type: this.getStudyModelType(hardware.model),
          serial
        }
      }
      return null
    },
    getDeviceDetailsByStudyTypeSerial(serial: string): SubscriptionDevice | null {
      if (this.studyDevices.has(serial)) {
        const hardware = this.studyDevices.get(serial)
        if (!hardware) return null
        return {
          name: hardware.name || this.$t('manageSubscriptions.notApplicable'),
          type: this.getStudyModelType(hardware.model),
          serial
        }
      }
      return null
    },
    getDeviceTypeDetails(deviceId: string): string {
      const model =
        (this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`](
          deviceId
        ) as string) || ''
      const submodel =
        (this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SUBMODEL_BY_DEVICE_ID}`](
          deviceId
        ) as string) || ''
      const modelKey = model
        ? (this.$store.getters[
            `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`
          ](model) as string)
        : ''
      const submodelKey = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SUBMODEL_KEY}`
      ](model, submodel) as string
      const deviceModelName = deviceModelDisplay(modelKey, this.$t.bind(this))
      const deviceSubmodelName = deviceSubmodelDisplay(submodelKey, this.$t.bind(this))
      return submodel ? `${deviceModelName} (${deviceSubmodelName})` : deviceModelName
    },
    getMobileHeaders(
      headers: DataTableHeader<SubscriptionTableRow>[][],
      item: SubscriptionTableRow
    ): MobileHeader[] {
      const headerRow = (toRaw(headers[0]) ?? []) as DataTableHeader<SubscriptionTableRow>[]
      return headerRow
        .filter(
          (
            header
          ): header is DataTableHeader<SubscriptionTableRow> & {
            key: keyof SubscriptionTableRow
            title: string
          } =>
            typeof header?.title === 'string' &&
            typeof header?.key === 'string' &&
            ![
              'subscription',
              'data-table-expand',
              'data-table-group',
              'data-table-select'
            ].includes(header.key) &&
            Object.hasOwn(item, header.key)
        )
        .map(({ key, title }) => ({ key, title }))
    },
    isDeviceOwned(deviceId: string): boolean {
      return (
        this.accountId ===
        this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ACCOUNT_BY_DEVICE_ID}`](
          deviceId
        )
      )
    },
    isSubscriptionExpandable(subscription: SubscriptionTableRow): boolean {
      if (
        subscription.subscriptionId === subscriptionTiers.TSILINK_STANDARD ||
        subscription.subscriptionId === subscriptionTiers.TSILINK_PLUS
      ) {
        return true
      }
      return !!subscription.deviceQuantityUsed
    },
    isRowExpanded(activationId: string): boolean {
      return this.expanded.includes(activationId)
    },
    toggleExpandedById(activationId: string): void {
      if (this.isRowExpanded(activationId)) {
        this.expanded = this.expanded.filter((id) => id !== activationId)
      } else {
        this.expanded.push(activationId)
      }
    },
    autoExpandDevices(): void {
      this.tableComputedItems.forEach((subscription) => {
        let foundSerial = false
        if (this.search && this.search.length >= 4) {
          subscription.devices.forEach((device) => {
            if (device.serial.includes(this.search.toString())) {
              foundSerial = true
            }
          })
        }
        if (foundSerial) {
          this.expandSubscriptionDevices(subscription)
        }
      })
    },
    expandAllDevices(): void {
      this.expanded = this.expandableSubscriptions.map((subscription) => subscription.activationId)
    },
    expandSubscriptionDevices(subscription: SubscriptionTableRow): void {
      if (!this.isRowExpanded(subscription.activationId)) {
        this.expanded.push(subscription.activationId)
      }
    },
    collapseAllDevices(): void {
      this.expanded = []
    },
    collapseSubscriptionDevices(subscription: SubscriptionTableRow): void {
      this.expanded = this.expanded.filter((id) => id !== subscription.activationId)
    },
    filterByColumnValueAndDeviceSerial(
      value: unknown,
      search: unknown,
      item: TableFilterItem
    ): boolean {
      const row = this.getTableItem(item.raw)
      const foundColumnValue =
        value != null &&
        search != null &&
        value.toString().toLowerCase().includes(search.toString().toLowerCase()) // Filter by column values

      if (!foundColumnValue && search != null && search.toString().length >= 4) {
        // Else filter by serial number
        return row.devices.findIndex((device) => device.serial.includes(search.toString())) !== -1
      }

      return foundColumnValue
    },
    /**
     * Tracks the currently visible (paginated/sorted/filtered) table rows so
     * autoExpandDevices can react to search changes. Display formatting
     * (N/A, GB suffix, addon usage text) is applied once, up-front, in
     * subscriptionToTableHelper/applySubscriptionDisplayOverrides — not here.
     */
    updateTableComputedItems(computedItems: SubscriptionTableRow[]): void {
      this.tableComputedItems = computedItems.map((item) => this.getTableItem(item))
      this.autoExpandDevices()
    },
    itemCanBeExpanded(item: SubscriptionTableRow): boolean {
      const subId = item.subscriptionId
      if (
        subId === subscriptionTiers.TSILINK_STANDARD ||
        subId === subscriptionTiers.TSILINK_PLUS
      ) {
        return true
      }
      if (this.isAddOnSub(subId)) {
        return false
      }
      const dqu = item.deviceQuantityUsed
      return !!(dqu && dqu !== this.$t('manageSubscriptions.notApplicable'))
    }
  },
  computed: {
    enableFileCount(): boolean {
      return this.featureFlagsStore.featureFlagBySlug(FeatureFlag.FileImportCountEnabled)
    },
    genericTSILinkSubs(): EnrichedSubscription[] {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_GENERIC_TSI_LINK}`
      ] as EnrichedSubscription[]
    },
    basicSubscriptions(): EnrichedSubscription[] {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_BASIC}`
      ] as EnrichedSubscription[]
    },
    premiumSubscriptions(): EnrichedSubscription[] {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_PREMIUM}`
      ] as EnrichedSubscription[]
    },
    dataSubscriptions(): EnrichedSubscription[] {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_DATA_SERVICES}`
      ] as EnrichedSubscription[]
    },
    isMobile(): boolean {
      return this.smAndDown
    },
    userSubscriptions(): SubscriptionTableRow[] {
      const userSubs: EnrichedSubscription[] = []
      userSubs.push(
        ...this.basicSubscriptions,
        ...this.premiumSubscriptions,
        ...this.dataSubscriptions,
        ...this.genericTSILinkSubs
      )
      return this.subscriptionToTableHelper(userSubs)
    },
    expandableSubscriptions(): SubscriptionTableRow[] {
      return this.userSubscriptions.filter((subscription) => {
        return this.isSubscriptionExpandable(subscription)
      })
    },
    customTableFilter(): string {
      if (this.search.length >= 2) return this.search
      if (this.search.length === 1) return '//no-result//'
      return ''
    },
    enableExpandableRows(): boolean {
      return !!(
        this.featureFlagsStore.featureFlagBySlug(FeatureFlag.ExpandManageSubscriptionsRows) &&
        this.expandableSubscriptions.length
      )
    },
    isLastRowExpanded(): boolean {
      const lastRow = this.tableComputedItems.length
        ? this.tableComputedItems[this.tableComputedItems.length - 1]
        : null
      return lastRow ? this.isRowExpanded(lastRow.activationId) : false
    },
    isLogInAs(): boolean {
      return this.loginas.getIsLogInAs
    }
  }
})
</script>

<style lang="scss" scoped>
.manage-subscriptions-card {
  .v-card-item {
    padding: 16px;

    :deep(.v-card-item__prepend) {
      align-self: start;
      padding-block: 5px;
    }
  }

  .header-title {
    font-size: 1.5rem;
    font-weight: 300;
  }

  .header-row {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .route-title {
    line-height: 1;
  }

  .divider {
    margin-left: 16px;
    margin-right: 16px;
    margin-bottom: 24px;
  }

  .timezoneDetails {
    opacity: 1;
  }

  .emptyMessage {
    height: 240px;
    line-height: 240px;
    color: var(--tsi-color--dark-56);
  }

  .table-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    background-color: transparent;
    margin-left: 16px;
    margin-right: 16px;
    margin-bottom: 16px;
    min-height: 48px;

    .search-field {
      max-width: 50%;

      :deep(input) {
        padding: 8px 0px;
      }
    }
  }

  &.is-mobile {
    .search-field {
      max-width: 100%;
    }
  }

  :deep(.v-data-table-footer) {
    padding: 8px 16px;
  }
}

.v-data-table {
  :deep(.v-data-table-header-mobile__select) {
    min-width: unset !important;
  }

  :deep(.v-data-table__empty-wrapper) {
    display: none;
  }

  :deep(.v-data-table__tr):has(.table-expand-btn--active) {
    .v-data-table__td {
      border-bottom: 0;
    }
  }

  td.mobile-list {
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: 48px;
    width: 100%;

    .item-title {
      display: flex;
      line-height: 36px;
      padding-top: 8px;
      .subscription-name {
        margin-left: 8px;
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    ul {
      padding-top: 8px;
      padding-bottom: 16px;
      padding-left: 12px;
      list-style-type: none;
      .item-title {
        font-weight: 500;
        font-size: 15px;
      }
    }
    ul.expandable {
      padding-bottom: 0px;
    }
    .mobile-action-menu-container {
      position: absolute;
      top: 16px;
      right: 16px;
    }
    .flex-item {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-bottom: 4px;
      > span:first-child {
        display: inline-block;
        margin-right: 4px;
        white-space: nowrap;
        color: rgba(0, 0, 0, 0.6);
      }
      > span:last-child {
        display: inline-block;
      }
    }
  }
}

.expandable-content-column:not(.mobile) {
  border-bottom: 3px dotted rgba(0, 0, 0, 0.3) !important;
}

:deep(.last-row-expanded:not(.mobile)) {
  .v-table__wrapper + .v-divider {
    display: none;
  }
}
</style>
